import React, { useState, useEffect, useCallback } from 'react';
import { Seccion, Grado } from '../types/usuario.types';
import { seccionService } from '../services/seccionService';
import { catalogoService } from '../services/usuarioService';
import SeccionFormPage from './SeccionFormPage';

// ============================================================
// SeccionesPage – Lista de secciones con CRUD completo
// ============================================================

type Vista = 'list' | 'new' | 'edit';

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS   = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_SEARCH = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_EDIT   = 'M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65ZM3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z';
const D_DEL    = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const BTN_COLOR: Record<string, string> = {
  blue: 'hover:text-blue-600 hover:bg-blue-50',
  red:  'hover:text-red-500  hover:bg-red-50',
};

const ActionBtn: React.FC<{ title: string; color: string; onClick: () => void; children: React.ReactNode }> =
  ({ title, color, onClick, children }) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded-lg text-slate-400 transition-colors ${BTN_COLOR[color]}`}>
      {children}
    </button>
  );

// ── Colores para letras de sección ────────────────────────────
const SECCION_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-pink-100 text-pink-700',
  'bg-indigo-100 text-indigo-700',
];
const getSeccionColor = (nombre: string) =>
  SECCION_COLORS[nombre.charCodeAt(0) % SECCION_COLORS.length];

// ── Componente principal ──────────────────────────────────────
const SeccionesPage: React.FC = () => {
  const [vista, setVista]   = useState<Vista>('list');
  const [editId, setEditId] = useState<number | null>(null);

  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [grados, setGrados]       = useState<Grado[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const [search, setSearch]             = useState('');
  const [gradoFilter, setGradoFilter]   = useState('');
  const [activoFilter, setActivoFilter] = useState('');

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadSecciones = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [data, grs] = await Promise.all([
        seccionService.listar(),
        catalogoService.getGrados(),
      ]);
      setSecciones(data);
      setGrados(grs);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar secciones');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSecciones(); }, [loadSecciones]);

  const handleSuccess = () => { setVista('list'); loadSecciones(); };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await seccionService.eliminar(deleteId);
      setDeleteId(null);
      loadSecciones();
    } catch { /* el httpClient ya lanza el error */ }
    finally { setDeleting(false); }
  };

  const getNombreGrado = (gradoId: number) =>
    grados.find(g => g.idGrado === gradoId)?.nombre ?? '—';

  const seccionesFiltradas = secciones.filter(s => {
    const matchSearch = search === '' || s.nombre.toLowerCase().includes(search.toLowerCase());
    const matchGrado  = gradoFilter === '' || String(s.gradoId) === gradoFilter;
    const matchActivo = activoFilter === '' || String(s.activo) === activoFilter;
    return matchSearch && matchGrado && matchActivo;
  });

  if (vista === 'new' || vista === 'edit') {
    return (
      <SeccionFormPage
        idSeccion={vista === 'edit' ? editId! : undefined}
        onSuccess={handleSuccess}
        onCancel={() => setVista('list')}
      />
    );
  }

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Gestión de Secciones</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {secciones.length} sección{secciones.length !== 1 ? 'es' : ''} registrada{secciones.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={() => setVista('new')}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                     font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
          <Ico d={D_PLUS} /> Registrar Sección
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Ico d={D_SEARCH} />
            </span>
            <input type="text" placeholder="Buscar por nombre..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                         text-sm text-slate-800 placeholder-slate-300 outline-none
                         focus:ring-2 focus:ring-blue-500 transition-all" />
          </div>
          <select value={gradoFilter}
            onChange={e => setGradoFilter(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]">
            <option value="">Todos los grados</option>
            {grados.map(g => (
              <option key={g.idGrado} value={g.idGrado}>{g.nombre} – {g.nivel}</option>
            ))}
          </select>
          <select value={activoFilter}
            onChange={e => setActivoFilter(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
            <option value="">Todas</option>
            <option value="true">Activas</option>
            <option value="false">Inactivas</option>
          </select>
        </div>
      </div>

      {/* Grilla de tarjetas */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-center py-16 text-red-500 text-sm">{error}</div>
        ) : seccionesFiltradas.length === 0 ? (
          <div className="text-center py-16">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
              className="w-12 h-12 text-slate-200 mx-auto mb-3">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z"/>
            </svg>
            <p className="text-slate-400 text-sm font-medium">No se encontraron secciones</p>
            <p className="text-slate-300 text-xs mt-1">Cambia los filtros o registra una nueva</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {['Sección', 'Grado', 'Capacidad', 'Estado', 'Acciones'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {seccionesFiltradas.map(s => (
                  <tr key={s.idSeccion} className="hover:bg-slate-50 transition-colors">

                    {/* Nombre */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center
                                         font-bold text-base ${getSeccionColor(s.nombre)}`}>
                          {s.nombre}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 text-sm">Sección {s.nombre}</p>
                          <p className="text-slate-400 text-xs">ID: {s.idSeccion}</p>
                        </div>
                      </div>
                    </td>

                    {/* Grado */}
                    <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-600 font-medium">
                        {getNombreGrado(s.gradoId)}
                      </span>
                    </td>

                    {/* Capacidad */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="text-sm font-semibold text-slate-700">
                        {s.capacidad ?? 40}
                        <span className="text-slate-400 font-normal text-xs ml-1">alumnos</span>
                      </span>
                    </td>

                    {/* Estado */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full
                        ${s.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${s.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {s.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-0.5">
                        <ActionBtn title="Editar sección" color="blue"
                          onClick={() => { setEditId(s.idSeccion); setVista('edit'); }}>
                          <Ico d={D_EDIT} />
                        </ActionBtn>
                        <ActionBtn title="Eliminar sección" color="red"
                          onClick={() => setDeleteId(s.idSeccion)}>
                          <Ico d={D_DEL} />
                        </ActionBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && seccionesFiltradas.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-400">
              Mostrando {seccionesFiltradas.length} de {secciones.length} sección{secciones.length !== 1 ? 'es' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Modal eliminación */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                  <Ico d={D_DEL} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Eliminar Sección</h3>
                  <p className="text-slate-400 text-xs">
                    Sección {secciones.find(s => s.idSeccion === deleteId)?.nombre}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-slate-600 text-sm">¿Estás seguro? Esta acción no se puede deshacer.</p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setDeleteId(null)} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold
                           text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-sm font-bold
                           text-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {deleting && (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                  </svg>
                )}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeccionesPage;
